import { z } from 'zod';
import { sessionNoyauEventSchema } from './noyau.js';
import {
  objectiveStatusSchema,
  protocolLocalIdSchema,
  storedCriterionThresholdSchema,
} from './protocol.js';
import { sessionStatusEventSchema, sessionVariablesSchema } from './session.js';

/**
 * ONE TURN of the engine (refonte modulaire §8.1 to §8.7, §9.5) - what the
 * scribe answers, what the engine decides, what the session records, and the
 * frames the conversation travels on.
 *
 * ## What is here and what is not
 *
 * The engine of §8 has two seats around a pure core: the scribe judges, the
 * interviewer speaks, and between them nothing but code decides. This file is
 * the CONTRACT of that core - the shapes crossing the boundary between a model
 * and the engine, and between the engine and the two screens that read a turn
 * (the person's conversation, §9.5; the operator's reader, §12.2). The
 * decisions themselves live in `../engine/`, pure, with no clock and no random.
 *
 * No prompt, no model call and no table is named here: the server lot (#64)
 * owns those, and the client lot (#65) owns the bubbles. Both read this file.
 *
 * ## An LLM answer is an external input
 *
 * `scribeReportSchema` is the one schema of this repo whose producer is a
 * MODEL. It is parsed before anything believes it, and a report that fails the
 * parse is not an error of the session: the turn is marked `malformed`, changes
 * no status and counts as played (§8.1). That is why every field here is
 * bounded and none is `.strict()` - a model that adds a key must not cost a
 * turn, a model that drops the scores must.
 *
 * ## Stored shapes are not `.strict()`
 *
 * A turn row keeps its blobs for the life of the session (§8.7), and the
 * reader of §12.2 replays a turn played months ago. Same rule as §4.1: a field
 * added later must not make today's rows unreadable. The two shapes that are
 * `.strict()` are the ones a CLIENT sends (`sessionMessageSchema`), where an
 * unknown key is a bug worth failing on.
 */

/* ------------------------------------------------------------- the scribe */

/** Bounds of what a model may answer. Met by a defect, never on purpose. */
export const SCRIBE_EXTRACT_MAX = 4000;
export const SCRIBE_EXTRACTS_MAX = 30;
export const SCRIBE_JUSTIFICATION_MAX = 600;

/** The person's message, at the régime of `CHAT_MESSAGE_MAX_CHARS`. */
export const SESSION_MESSAGE_MAX_CHARS = 20_000;

/**
 * One criterion of one objective, as the scribe SCORES it (§8.2): a number from
 * 0 to 100 and a one-sentence justification.
 *
 * The form a criterion calls for is fixed by its FROZEN version (§5.2, §6): a
 * scored criterion takes this one, « une note de 0 à 100 et rien d'autre, pas
 * de verdict ». The scribe knows neither the bands' plancher, nor the
 * threshold, nor the expected quantity: it notes, the code compares
 * (`criterionSatisfied`), and the reader of §12.3 shows the score beside the
 * threshold with the band it falls in. Handing it the bar would make the model
 * the judge of its own answer.
 *
 * The score is noted on the CUMULATED matter of the objective - every extract
 * attributed up to this turn - and not on the turn's reply alone (H19, §16).
 */
export const scribeCriterionScoreSchema = z.object({
  criterionId: z.string().min(1),
  score: z.number().int().min(0).max(100),
  justification: z.string().trim().max(SCRIBE_JUSTIFICATION_MAX).default(''),
});
export type ScribeCriterionScore = z.infer<typeof scribeCriterionScoreSchema>;

/**
 * The OTHER form (§8.2, grill of 2026-09-16): a criterion whose frozen version
 * carries no notation is judged « rempli ou non », and the scribe renders a
 * boolean.
 *
 * There is no score to render and none to invent: « un critère non noté n'a pas
 * de note à comparer » (§8.2, retiring H18). A verdict is not a 0 or a 100 in
 * disguise - a false verdict beside a « 0 » would read as a score somebody
 * measured, and the reader of §12.3 words the two differently on purpose.
 */
export const scribeCriterionVerdictSchema = z.object({
  criterionId: z.string().min(1),
  verdict: z.boolean(),
  justification: z.string().trim().max(SCRIBE_JUSTIFICATION_MAX).default(''),
});
export type ScribeCriterionVerdict = z.infer<typeof scribeCriterionVerdictSchema>;

/**
 * One criterion judged, in EITHER form - « une note de 0 à 100 » or « un
 * verdict, rempli ou non » (§8.2).
 *
 * A union rather than one object with two optional fields, so the TYPE says one
 * or the other: a reader narrows on `'score' in judgement` and cannot reach a
 * judgement that carries neither.
 *
 * ## Whether the form is the RIGHT one is not a question Zod can answer
 *
 * Which form a criterion calls for is written in its frozen version, and this
 * file knows nothing of criteria. So the parse accepts both shapes and
 * `misjudgedCriteria` (engine/objectives) compares them against the frozen
 * scales; a score on an unscored criterion, or a verdict on a scored one, marks
 * the turn MALFORMED (§8.1) exactly as an unparseable answer does.
 *
 * A judgement carrying BOTH keys reads as the score - the first branch of the
 * union - and the frozen form then settles it: on a scored criterion it is the
 * answer that was asked for and the stray key is ignored (no `.strict()` on a
 * model's output, see the head of this file), on an unscored one it is a
 * malformed turn. A model that answers two questions at once is not given the
 * benefit of whichever one happens to fit.
 */
export const scribeCriterionJudgementSchema = z.union([
  scribeCriterionScoreSchema,
  scribeCriterionVerdictSchema,
]);
export type ScribeCriterionJudgement = z.infer<typeof scribeCriterionJudgementSchema>;

/**
 * What the scribe answers for ONE open objective of the current step (§8.2):
 * the verbatim extracts it attributes to it, and a judgement per criterion.
 *
 * « La matière d'un objectif, ce sont ces extraits et rien d'autre. » An
 * extract absent from the person's verbatim is rejected by `literalExtracts`
 * without breaking the turn - a model that paraphrases loses the extract, not
 * the session.
 */
export const scribeObjectiveReadingSchema = z.object({
  objectiveId: protocolLocalIdSchema,
  extracts: z.array(z.string().max(SCRIBE_EXTRACT_MAX)).max(SCRIBE_EXTRACTS_MAX).default([]),
  criteria: z.array(scribeCriterionJudgementSchema).default([]),
});
export type ScribeObjectiveReading = z.infer<typeof scribeObjectiveReadingSchema>;

/**
 * An explicit refusal of the person (§7.3): « je ne veux pas en parler ».
 *
 * `objectiveId` names what is refused; null when the refusal aims at nothing
 * nameable, in which case it fails no objective on its own. A refusal is one
 * of the two ways an objective reaches `raté`, the other being the end of the
 * step without success.
 */
export const scribeRefusalSchema = z.object({
  detected: z.boolean().default(false),
  objectiveId: protocolLocalIdSchema.nullable().default(null),
});
export type ScribeRefusal = z.infer<typeof scribeRefusalSchema>;

/** Per global guardrail (§8.5): did the turn show what its detection describes. */
export const scribeGuardrailReadingSchema = z.object({
  guardrailId: z.string().min(1),
  triggered: z.boolean(),
});
export type ScribeGuardrailReading = z.infer<typeof scribeGuardrailReadingSchema>;

/**
 * Per global rule (§8.5): did the LAST message of the AI break it.
 *
 * Observation only, with no action attached - « la boucle de relecture du
 * cycle précédent, obtenue par le siège qui lit déjà chaque tour ». The engine
 * reads these and decides nothing on them.
 */
export const scribeRuleReadingSchema = z.object({
  ruleId: z.string().min(1),
  violated: z.boolean(),
});
export type ScribeRuleReading = z.infer<typeof scribeRuleReadingSchema>;

/**
 * The whole contract of the scribe for one turn (§8.2).
 *
 * `answered` is the scribe's half of the empty answer (H4 hybride, #83): « la
 * personne a-t-elle répondu à la question, même sans apporter de matière ».
 * « Je ne sais pas » is an answer; a blank or a total non-sequitur is not. The
 * engine reads it beside the count of literal extracts, and a turn is empty
 * only when both say so (`applyReading`).
 *
 * It DEFAULTS TO TRUE, and the default is the decision: the rows written before
 * the field existed, and a model that forgets it, must never count as empty -
 * the streak only ever CLOSES steps, so a hole in the report has to fall on the
 * side that closes nothing. The same régime as the other defaults of this
 * schema, which soften a stored blob at read time (see `session/turns.ts`).
 */
export const scribeReportSchema = z.object({
  objectives: z.array(scribeObjectiveReadingSchema).default([]),
  refusal: scribeRefusalSchema.default({ detected: false, objectiveId: null }),
  answered: z.boolean().default(true),
  guardrails: z.array(scribeGuardrailReadingSchema).default([]),
  rules: z.array(scribeRuleReadingSchema).default([]),
});
export type ScribeReport = z.infer<typeof scribeReportSchema>;

/** A report that says nothing - what a turn with no open objective reads as. */
export const EMPTY_SCRIBE_REPORT: ScribeReport = {
  objectives: [],
  refusal: { detected: false, objectiveId: null },
  answered: true,
  guardrails: [],
  rules: [],
};

/* ----------------------------------------------------------- the decision */

/**
 * WHERE the session goes at the end of a turn (§8.3, §8.4, §8.5).
 *
 * The same six shapes whatever produced them - a guardrail, a condition or the
 * implicit logic - because the caller that plays them must not branch on the
 * producer. `close-step` never reaches a reader on the LAST step: closing the
 * last step closes the protocol (§8.3), and the engine normalises it.
 */
export const turnOutcomeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('continue') }),
  z.object({ kind: z.literal('close-step') }),
  z.object({ kind: z.literal('replay-step') }),
  z.object({ kind: z.literal('go-to-step'), stepId: protocolLocalIdSchema }),
  z.object({ kind: z.literal('close-protocol') }),
  z.object({
    kind: z.literal('close-experience'),
    /** The step's own closing text, the action's, or the default of §7.2. */
    text: z.string().trim().min(1),
  }),
]);
export type TurnOutcome = z.infer<typeof turnOutcomeSchema>;
export type TurnOutcomeKind = TurnOutcome['kind'];

/**
 * « La règle, la condition ou le garde-fou qui l'a produite » (§8.7).
 *
 * `id` names the guardrail or the condition; it is null for the implicit
 * logic, which has no identifier because it is not written anywhere - it is
 * what the engine does when nothing was written (§8.3).
 */
export const decisionOriginSchema = z.object({
  kind: z.enum(['guardrail', 'condition', 'implicit']),
  id: z.string().min(1).nullable().default(null),
});
export type DecisionOrigin = z.infer<typeof decisionOriginSchema>;

/**
 * WHICH of the three caps of §8.3 the engine ACTED ON for this turn, or null.
 *
 * §8.3 gives a step « trois façons d'atteindre le plafond » - the turn counter,
 * the streak of empty answers, the AI budget of §11.2 - and states the three
 * « valent exactement plafond atteint ». They are therefore interchangeable to
 * the engine and NOT to whoever reads a session back: a step closed on its
 * fifth turn out of twelve, with no empty answer, was closed by the budget, and
 * nothing recorded said so. It had to be deduced by elimination, which is the
 * incident of issue #97.
 *
 * It names the FIRST cap that is true, in the order of the section
 * (`StepCap.by`), because that is the order the engine reads them in - naming
 * two would say the step was closed twice.
 *
 * Null is « no ceiling took part in this decision », and not « no ceiling was
 * standing »: a step that closes because its last objective succeeded closed on
 * its own success, whatever the counters were worth on that turn, and where the
 * step stands against its three ceilings is on the snapshot. Writing a cap
 * under a decision it did not cause would hand a reader the deduction back.
 *
 * `.default(null)` and never required, the régime of every field added to a
 * stored shape here (§4.1): a decision written before this field existed reads
 * back as « no cap », which is what an unknown is worth.
 */
export const stepCapReachedSchema = z
  .enum(['turns', 'empty-answers', 'budget'])
  .nullable()
  .default(null);
export type StepCapReached = z.infer<typeof stepCapReachedSchema>;

/**
 * The whole decision of one turn: where the session goes, what the interviewer
 * is told, which objectives were emptied, and who decided.
 *
 * `directive` is the INJECTED one - the « direction d'IA » of a condition or
 * of a guardrail, served to the interviewer for the next message (§8.4, §8.5).
 * It is null on a decision nobody wrote a direction for; « ce qui manque » is
 * not a directive and is computed apart, by `engineDirective`.
 *
 * `invalidated` is the non-terminal half of a condition's actions (§8.4):
 * every objective sent back to `vide`, its extracts kept in the journal and
 * marked invalidated. It rides on the decision rather than on the snapshot
 * alone because §8.7 records « la décision du moteur » and « invalider
 * Contexte de vie, puis aller à 3 · Contexte » is one decision, not two.
 *
 * `cap` names the ceiling of §8.3 the decision was taken ON, whoever took it:
 * the implicit logic on the branches a cap guards, a condition that was
 * listening to a ceiling, and never a guardrail, which wins before the counters
 * advance. It is a second fact beside `producedBy` and not a half of it - « qui
 * a décidé » and « contre quoi » (§8.7) - and null when the answer to the
 * second is « nothing ».
 */
export const turnDecisionSchema = z.object({
  outcome: turnOutcomeSchema,
  directive: z.string().nullable().default(null),
  invalidated: z.array(protocolLocalIdSchema).default([]),
  producedBy: decisionOriginSchema,
  cap: stepCapReachedSchema,
});
export type TurnDecision = z.infer<typeof turnDecisionSchema>;

/* ----------------------------------------------------------- the snapshot */

/**
 * One extract of an objective's matter, in the journal of the session.
 *
 * An invalidated extract STAYS - « ses extraits restent dans le journal de la
 * session marqués invalidés, sa matière vivante repart de zéro » (§8.4). So
 * the flag is on the extract and the extract is never dropped: the reader of
 * §12.2 shows what was said and what stopped counting.
 */
export const snapshotExtractSchema = z.object({
  text: z.string(),
  invalidated: z.boolean().default(false),
});
export type SnapshotExtract = z.infer<typeof snapshotExtractSchema>;

/**
 * The band a score fell in, frozen with it (§8.2): « 62 · tranche 50-75 ».
 *
 * Lower included, upper excluded, the last band including 100 - the rule of §6,
 * and the reader prints the two numbers rather than re-deriving them: the
 * bands belong to the criterion's frozen version, and a reader that resolved
 * them again would show today's scale beside a score measured on another one.
 */
export const snapshotBandSchema = z.object({
  lower: z.number().int().min(0).max(100),
  upper: z.number().int().min(0).max(100),
});
export type SnapshotBand = z.infer<typeof snapshotBandSchema>;

/**
 * One criterion of one objective, as the snapshot froze it (§8.7): what the
 * scribe rendered, what it was compared against, and whether it passed.
 *
 * ## Everything the reader needs, so it never re-reads a criterion version
 *
 * « Verdict ou note de chaque critère avec son seuil ou son plancher » (§8.7).
 * The bar travels BESIDE the judgement because an objective edited since - or a
 * criterion whose scale moved - would otherwise make the replay of an old turn
 * read against today's rule. So a scored criterion freezes its `plancher` and
 * the `band` its score fell in, and an unscored one freezes its `verdict`; the
 * screens of §12.3 print those and resolve nothing.
 *
 * `score` and `verdict` are both null while the scribe has not judged this
 * criterion yet - never a zero and never a false, both of which are judgements.
 * `plancher` null is the mark of an UNSCORED criterion: it is what tells the
 * reader (and `criterionSatisfied`) which of the two forms this line is.
 *
 * The garde au gel of H47 (§8.2) needs no field of its own: a threshold beside
 * a null plancher IS a threshold the engine ignored, and the reader of §12.3
 * says so from those two. A third field would be a second spelling of one fact,
 * with the usual consequence - the two producers of this shape disagreed about
 * it before the ink was dry.
 *
 * `threshold` borrows `storedCriterionThresholdSchema` rather than restating a
 * number, which is what makes H46 reach the turns ALREADY WRITTEN: a snapshot
 * frozen before the grill of 2026-09-16 carries `{ op, value }`, and the reader
 * of §12.3 has to open it without the session being replayed. For the same
 * reason every field added here carries a read DEFAULT (§4.1): a row written
 * before this MR has no `verdict`, no `plancher` and no `band`, and it still
 * parses - with `satisfied` as its own turn decided it, which is the one figure
 * that must never be recomputed.
 */
export const snapshotCriterionSchema = z.object({
  criterionId: z.string().min(1),
  /** The verdict of an UNSCORED criterion; null on a scored one, or unjudged. */
  verdict: z.boolean().nullable().default(null),
  score: z.number().int().min(0).max(100).nullable().default(null),
  threshold: storedCriterionThresholdSchema.default(null),
  /** The plancher of the frozen scale; null when the frozen version is unscored. */
  plancher: z.number().int().min(0).max(100).nullable().default(null),
  band: snapshotBandSchema.nullable().default(null),
  satisfied: z.boolean().default(false),
  /**
   * Where « passable » begins on the frozen scale (§6, issue #110): the lower
   * bound of its passable band, frozen as a NUMBER like the plancher, or null
   * when the scale marks none. `satisfied` decides every turn; this bound only
   * counts at the END of a step, where an objective whose criteria all reach it
   * - its quantity reached - settles réussi at the passable bar (§7.3, issue
   * #112). Until then it tells the reader of §12.3 whether a miss was close.
   */
  passable: z.number().int().min(0).max(100).nullable().default(null),
});
export type SnapshotCriterion = z.infer<typeof snapshotCriterionSchema>;

/** One objective's whole state: its status, its matter, its scores (§8.7). */
export const snapshotObjectiveSchema = z.object({
  objectiveId: protocolLocalIdSchema,
  status: objectiveStatusSchema,
  extracts: z.array(snapshotExtractSchema).default([]),
  criteria: z.array(snapshotCriterionSchema).default([]),
  /**
   * Réussi « en orange » (§7.3, issue #112): the end of the step validated this
   * objective on the PASSABLE bar - every criterion filled or at its passable
   * bound, the quantity reached - instead of failing it. Its status is
   * `succeeded` and it counts as one wherever the protocol decides; only a
   * reader paints it apart. ABSENT otherwise - on every objective written
   * before #112, and on one an invalidation rebuilt - so a fixture or a stored
   * row that does not name it reads as the plain status it always was.
   */
  passableOnly: z.boolean().optional(),
});
export type SnapshotObjective = z.infer<typeof snapshotObjectiveSchema>;

/**
 * « Un instantané de l'état du protocole » (§8.7), and the STATE the pure
 * engine reads: the same object answers both needs, because a snapshot that
 * could not be fed back to the engine would not replay anything.
 *
 * `objectives` holds an entry per objective the engine has MET - judged once,
 * or emptied by an invalidation - and not one per objective the protocol
 * carries. An objective with no entry is `vide` with no matter, which is what
 * a reader shows and what `{{objectif:id}}` renders as « rien encore » (§8.6).
 * Holding the whole protocol would mean handing the engine a content it does
 * not need, and an objective of a PREVIOUS step has to be here anyway: an
 * invalidation may aim at one (H20) and a direction may quote one.
 *
 * `guardrailStreaks` is the one field §8.7 does not list, and it is here for a
 * reason the section could not know: « pendant N tours consécutifs » (§8.5) is
 * STATE, and a pure engine reads its state from its arguments. Keyed by
 * guardrail id, it holds how many consecutive turns each one has rendered
 * true; a guardrail the scribe did not judge this turn leaves the record,
 * which is what keeps a guardrail deleted in Réglages from closing a session
 * on a streak nobody can see any more.
 *
 * `turnInStep`, `emptyAnswerStreak` and `stepCostEur` are the three counters
 * the cap of §8.3 is read on, and all three are counted PER ATTEMPT: a replay
 * puts them back to zero and keeps the statuses (§8.4).
 */
export const protocolSnapshotSchema = z.object({
  stepId: protocolLocalIdSchema,
  /** The pass of the step, 1-based; `attempts` of §7.2 is how many are allowed. */
  attempt: z.number().int().positive().default(1),
  turnInStep: z.number().int().nonnegative().default(0),
  emptyAnswerStreak: z.number().int().nonnegative().default(0),
  /** The cumulated cost of the model calls of THIS attempt, in euros (§11.2). */
  stepCostEur: z.number().nonnegative().default(0),
  objectives: z.array(snapshotObjectiveSchema).default([]),
  /** Every value the session has written so far (§8.6), by block or page id. */
  variables: sessionVariablesSchema.default({}),
  guardrailStreaks: z.record(z.string().min(1), z.number().int().nonnegative()).default({}),
});
export type ProtocolSnapshot = z.infer<typeof protocolSnapshotSchema>;

/* --------------------------------------------------------- the model calls */

/**
 * Which SEAT made a call (§8.1, §11.1). Two per turn: the scribe judges on the
 * person's answer, the interviewer writes the next message.
 *
 * Its own enum rather than `modelSeatKeySchema` of `models.ts`, and the
 * difference is the point. That one names the SETTINGS an operator fills, the
 * three cards of the models screen; this one names what a TURN did, so it holds
 * exactly the two seats a turn can call. Keeping them apart is what lets
 * Réglages gain, drop or rename a row without changing the meaning of rows
 * already stored in `session_turns` - which is exactly what #61 did, dropping
 * the two judges and adding the narrator without this enum moving a character.
 */
export const turnSeatSchema = z.enum(['interviewer', 'scribe']);
export type TurnSeat = z.infer<typeof turnSeatSchema>;

/** A provider's message, truncated where it is recorded, never logged whole. */
export const TURN_CALL_ERROR_MAX = 300;

/**
 * The bound of a provider's stop reason, as the WRITER clamps it. Generous for
 * a word: the longest one anybody sends is `stop_sequence`, and the margin is
 * there so a provider that invents a longer one still reads.
 *
 * The margin alone protects nothing - a longer value fails this schema, and a
 * whole turn with it - so `engine/conductor.ts` applies the bound before the
 * call is built (`storedStopReason`), which is where the same file already
 * truncates `failedReason`.
 */
export const TURN_CALL_STOP_REASON_MAX = 40;

/**
 * At most this many calls on one turn: the two seats, plus room for the
 * retries a flaky provider costs. A bound and not a promise - two is what a
 * healthy turn records.
 */
export const TURN_CALLS_MAX = 8;

/**
 * ONE model call of one turn (§8.7, §12.2) - what it asked of a provider and
 * what it cost, in every unit the operator's screens read.
 *
 * ## Why this rides on the turn rather than being read from Langfuse
 *
 * `llm/tracing.ts` states the rule it lives by: Langfuse is for « seeing a
 * call and cross-checking it », never for « producing a figure ». It is also
 * absent by default - no credentials, no trace, by design - so a screen that
 * read its tokens would have holes wherever a deployment is plain, and holes
 * exactly where somebody is trying to explain a bill. Every figure of Sessions
 * is therefore computed from what the turn itself recorded.
 *
 * ## The five fields that are not the cost
 *
 * - `model` is the id the API ANSWERED and never the one Réglages configured.
 *   A seat pointed at an alias, a router serving another revision, a provider
 *   quietly retiring a dated id: all three are readable here and nowhere else,
 *   which is why `LlmResponse` carries the returned model at all.
 * - the four token buckets are `LlmUsage` entire, and they stay decomposed
 *   because that is how a cost is arrived at (`llm/prices.ts`): a cached system
 *   prompt is written once and read afterwards at a tenth of the price, so one
 *   flattened « tokens » figure would make a long conversation look
 *   inexplicably cheap and an operator distrust the euro beside it.
 * - `durationMs` is what the seat took. It is the figure that says « the scribe
 *   is the slow half of the turn » before anybody profiles anything.
 * - `failedReason` is a call that never returned. Recorded rather than dropped,
 *   because a turn that paid for a retry is otherwise a turn whose cost has no
 *   explanation, and because a seat failing repeatedly is the first sign of an
 *   outage an operator can see without opening a log.
 * - `stopReason` is WHY the provider stopped writing, in its own spelling
 *   (issue #96). It is what tells an answer the model finished from one it was
 *   cut off in, and on the SCRIBE's call it is what makes a turn truncated
 *   (§8.1). Its own note is below.
 *
 * Not `.strict()`, like every stored shape (§4.1).
 */
export const turnCallSchema = z.object({
  seat: turnSeatSchema,
  /** What the API answered; null when the call never got that far. */
  model: z.string().min(1).nullable().default(null),
  inputTokens: z.number().int().nonnegative().default(0),
  outputTokens: z.number().int().nonnegative().default(0),
  cacheWriteTokens: z.number().int().nonnegative().default(0),
  cacheReadTokens: z.number().int().nonnegative().default(0),
  /** At this seat's own tariff (§11.1), in euros. */
  costEur: z.number().nonnegative().default(0),
  durationMs: z.number().int().nonnegative().default(0),
  startedAt: z.string().datetime(),
  /** Null on success; the provider's message, truncated, otherwise. */
  failedReason: z.string().max(TURN_CALL_ERROR_MAX).nullable().default(null),
  /**
   * WHY the provider stopped writing, in ITS OWN WORDS (issue #96).
   *
   * Stored as the provider spells it - `end_turn`, `max_tokens`,
   * `stop_sequence`, `tool_use`, `pause_turn`, `refusal` on Anthropic - and
   * never folded into an enum of ours. Two reasons, and the second is the one
   * that decided it: a provider that adds a reason tomorrow must not make a
   * stored row stop parsing, and a value nobody here anticipated is exactly
   * the value somebody will need to read off a turn that went wrong.
   *
   * The router spells the same fact differently (`finish_reason`), so
   * `llm/openrouter.ts` maps `length` onto `max_tokens` and `stop` onto
   * `end_turn` before it lands here: a screen that had to know which door a
   * call went through to read why it stopped would be a screen with two
   * spellings of one fact.
   *
   * Null on a call that never returned, and on every row written before this
   * field existed - which is why nothing derives from its absence.
   */
  stopReason: z.string().max(TURN_CALL_STOP_REASON_MAX).nullable().default(null),
});
export type TurnCall = z.infer<typeof turnCallSchema>;

/**
 * Every token of one call - the figure the reader of §12.2 adds up.
 *
 * It reads the four buckets and nothing else, so it takes any call that carries
 * them: the analyst's calls of the noyau are summed with it too (noyau.md §4.7,
 * #149), and a second spelling of « the tokens of a call » would be a second
 * count on the one screen that explains a bill.
 */
export function turnCallTokens(
  call: Pick<TurnCall, 'inputTokens' | 'outputTokens' | 'cacheWriteTokens' | 'cacheReadTokens'>,
): number {
  return call.inputTokens + call.outputTokens + call.cacheWriteTokens + call.cacheReadTokens;
}

/**
 * What a turn's calls cost together, in euros, rounded to the cent.
 *
 * The ONE place the arithmetic is written, and the writer of a turn uses it to
 * fill `costEur`: a second spelling of « what this turn cost » is a second
 * answer, and the two would part company on the first rounding.
 *
 * It reads the cost of each call and nothing else, so it takes any call that
 * carries one: the determination of the noyau sums its analyst's calls with it
 * (noyau.md §4.7, #149), and a second rounding for a second seat would be the
 * very drift this function exists to prevent.
 */
export function turnCostEur(calls: readonly Pick<TurnCall, 'costEur'>[]): number {
  const total = calls.reduce((sum, call) => sum + call.costEur, 0);
  return Math.round(total * 100) / 100;
}

/* ------------------------------------------------------- the served inputs */

/** The bound of one stored prompt text. Met by a defect, never on purpose. */
export const TURN_PROMPT_TEXT_MAX = 200_000;

/** One message of a served prompt, the shape `LlmMessage` already travels in. */
export const turnPromptMessageSchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string().max(TURN_PROMPT_TEXT_MAX),
});
export type TurnPromptMessage = z.infer<typeof turnPromptMessageSchema>;

/**
 * What ONE seat was actually SENT for one turn - the system text and the
 * messages, exactly as they left the server (§8.7).
 *
 * ## Why it is stored at all, rather than rebuilt on demand
 *
 * Because it cannot be rebuilt. What the interviewer receives (§10.3) is
 * assembled from the frozen persona, the step's direction, the objectives still
 * open, the engine's directive - and from the global RULES of §11.3, which live
 * in Réglages, are reordered and rewritten, and are frozen by nothing. The
 * assembling code changes with every merge request that touches `prompts/`. So
 * a prompt reconstructed later is correct until it silently is not, and the
 * moment it stops being correct is exactly the moment somebody re-reads an old
 * session to understand a bad question.
 *
 * ## Why it is a shape of its own, in a table of its own
 *
 * It is the largest thing a turn produces and the least often read. Turns are
 * loaded by the hundred to draw a conversation; this is opened one at a time,
 * by somebody who has already decided to look. Carried on `sessionTurnSchema`
 * it would be paid for on every read of every screen, so it lives in
 * `session_turn_prompts` behind its own route. That table is purged with the
 * session (§12.4): it holds the person's verbatim a second time, and an
 * erasure that left a copy behind would not be one.
 */
export const turnPromptSchema = z.object({
  seat: turnSeatSchema,
  system: z.string().max(TURN_PROMPT_TEXT_MAX).default(''),
  /**
   * Where the part of `system` that holds for the whole STEP ends (#131), in
   * UTF-16 code units - JavaScript's `length`, the unit the cut is measured and
   * applied in; a reader that counts code points lands elsewhere after any
   * emoji.
   *
   * The interviewer's system opens with what holds for a step - framing,
   * persona, overlay, Contexte, rules, the step's direction - and closes with
   * what changes on every turn - the open objectives, the directive (§10.3). The
   * Anthropic client puts its one cache point at this length and sends the rest
   * after it, outside the cache; the router sends no cache point at all, so the
   * field describes the PROMPT, never a cache that was or was not written.
   *
   * `system` itself does not change: it is still the whole text, exactly what
   * the model read and in the order of §10.3.
   *
   * Absent: the whole system holds for the step - the scribe's, whose system is
   * its step's instrument (#129) - or the prompt was stored before this field.
   */
  stableSystemLength: z.number().int().nonnegative().max(TURN_PROMPT_TEXT_MAX).optional(),
  messages: z.array(turnPromptMessageSchema).default([]),
});
export type TurnPrompt = z.infer<typeof turnPromptSchema>;

/** `GET /api/sessions/{id}/turns/{turnId}/prompts` - what both seats were served. */
export const turnPromptsSchema = z.object({
  turnId: z.string().min(1),
  prompts: z.array(turnPromptSchema).default([]),
});
export type TurnPrompts = z.infer<typeof turnPromptsSchema>;

/* --------------------------------------------------------------- the turn */

/**
 * An incident to show in Sessions (§8.5): a guardrail whose action is
 * « signaler seulement » and whose series reached its N on this turn.
 *
 * Only the flag-only ones are incidents. A guardrail that closes or injects is
 * visible as the DECISION's `producedBy`, and recording it twice would give
 * two places to read one fact. A rule the AI broke is not an incident either:
 * it is in the scribe's own reading, observation and nothing else (§8.2).
 */
export const turnIncidentSchema = z.object({
  guardrailId: z.string().min(1),
});
export type TurnIncident = z.infer<typeof turnIncidentSchema>;

/**
 * What a turn records (§8.7), entire. « La page Sessions rejoue l'état à
 * n'importe quel tour sans le recalculer », so everything that was true at the
 * end of the turn is on the row and nothing is derived at read time.
 *
 * Two texts that look alike and are not: `directive` is what the interviewer
 * was TOLD before writing `message` - the injected direction the previous turn
 * decided, or null - and `decision.directive` is what this turn decided for
 * the next message. Reading one row therefore tells what the interviewer knew
 * and what the engine answered, which is exactly what a replay needs.
 *
 * `scribe` is the VALIDATED reading and `scribeRaw` what the model actually
 * answered. A turn whose reading failed the parse carries `malformed: true`, a
 * null `scribe`, and the raw text - « un tour dont le scribe est illisible est
 * marqué malformé, ne change aucun statut, et compte comme un tour joué »
 * (§8.1). The raw text is kept precisely because that is the only turn nobody
 * can reconstruct.
 */
export const sessionTurnSchema = z.object({
  id: z.string().min(1),
  sessionId: z.string().min(1),
  /** 0-based, across the whole session and not per step. */
  index: z.number().int().nonnegative(),
  stepId: protocolLocalIdSchema,
  /** The interviewer's message, variables already resolved (§8.6). */
  message: z.string(),
  /**
   * The person's answer, verbatim - what `literalExtracts` is checked against.
   *
   * NULL on the farewell turn (issue #163, §8.1, §8.7): the goodbye is a
   * half-turn nobody answers, and null is how it is never mistaken for an
   * empty answer (H82).
   */
  reply: z.string().nullable(),
  /**
   * The REREAD ON ENTRY (issue #165, §8.1, §8.7, H94): a turn without an
   * exchange, written in the ARRIVING step before its first message - an empty
   * `message`, a null `reply`, ONE scribe call over the last pass of the step
   * just left, and the entry decision of §8.3 (H97). It counts in neither the
   * turn figures nor the malformed ones, and its cost counts (H100).
   *
   * False on every row written before the field existed, which is what they
   * were: no reread existed before #165.
   */
  reread: z.boolean().default(false),
  scribeRaw: z.string().default(''),
  scribe: scribeReportSchema.nullable().default(null),
  malformed: z.boolean().default(false),
  /**
   * The turn is malformed AND the scribe's call was cut at its ceiling (#96).
   *
   * A SUBSET of `malformed` and never an alternative to it: the engine effect
   * is identical - no status changes, the turn counts as played (§8.1) - so
   * every figure that counts malformed turns keeps counting this one, and
   * there is no ninth figure to add. What changes is the READING: « le scribe
   * a rendu n'importe quoi » and « le scribe a été coupé en plein rapport »
   * are two different defects, repaired in two different places, and a reader
   * told only the first would go looking for a prompt bug that is not there.
   *
   * False on every row written before the field existed, which is the honest
   * answer: those turns carry no stop reason to decide it on.
   */
  truncated: z.boolean().default(false),
  decision: turnDecisionSchema,
  directive: z.string().nullable().default(null),
  /**
   * The model calls of this turn (§8.1), one entry per seat called.
   *
   * Empty on a turn written before this field existed, which is why nothing
   * derives from its emptiness: `costEur` below stays the figure every screen
   * reads, and this is the breakdown behind it.
   */
  calls: z.array(turnCallSchema).max(TURN_CALLS_MAX).default([]),
  /**
   * What the two model calls of this turn cost (§8.1), in euros: the SUM of
   * `calls`, written by `turnCostEur`.
   *
   * Stored rather than summed at read time, and both halves of that are
   * deliberate. It is STORED because `protocolSnapshotSchema.stepCostEur`
   * accumulates it turn after turn and the engine is pure - it is handed a
   * number, it does not walk an array. It is a SUM because one figure with two
   * sources is one figure with two answers.
   */
  costEur: z.number().nonnegative().default(0),
  snapshot: protocolSnapshotSchema,
  incidents: z.array(turnIncidentSchema).default([]),
  createdAt: z.string().datetime(),
});
export type SessionTurn = z.infer<typeof sessionTurnSchema>;

/**
 * THE HALF-TURN waiting for an answer (§8.1) - what the session holds between
 * the interviewer speaking and the person replying.
 *
 * A turn is a PAIR: « le message servi, la réponse de la personne » (§8.7), and
 * `sessionTurnSchema` carries both because a row is written once, whole, when
 * the second half arrives. So the first half needs somewhere to live in the
 * meantime, and « in memory » is not that somewhere: a reprise redraws the
 * thread from `GET /api/s/{token}` (§5.3, §9.5), and a message lost to a deploy
 * would leave the person looking at a question that is no longer anywhere and
 * the engine judging their answer against a blank.
 *
 * ## Why the prompt and the call ride along
 *
 * Both belong to the MESSAGE and not to the moment it was produced. The
 * interviewer that writes the message of turn N+1 is called while turn N is
 * being played, so the call and the served input would otherwise be filed under
 * the turn that happened to trigger them - and the first message of a step,
 * which no turn triggers, would have nowhere to go at all. Carried here, they
 * land on the turn their message ends up on: a turn's `calls` are then exactly
 * the two calls that made it - the one that wrote its message, the one that
 * judged its reply - and every call is counted once, on one turn, in one step's
 * cost (§11.2).
 *
 * `id` is a correlation handle for the live channel and NOT the identifier of
 * the row this will become: the deltas of §9.5 have to name a bubble before any
 * turn exists, and the stored turn draws its own id when it is written. Nothing
 * reads it back afterwards - a reconnection re-reads the conversation by REST,
 * where the bubbles carry no id at all.
 *
 * Not `.strict()`, like every stored shape (§4.1).
 */
export const pendingTurnSchema = z.object({
  id: z.string().min(1),
  /** The step the message was written for - where the session stands. */
  stepId: protocolLocalIdSchema,
  /** The message as it was served, variables already resolved (§8.6). */
  message: z.string(),
  /** What the interviewer was TOLD before writing it, or null. */
  directive: z.string().nullable().default(null),
  /** The input that produced it, exactly as it left the server (§8.7). */
  prompt: turnPromptSchema,
  /** What writing it cost, at the interviewer's own tariff (§11.1). */
  call: turnCallSchema,
  createdAt: z.string().datetime(),
});
export type PendingTurn = z.infer<typeof pendingTurnSchema>;

/**
 * A turn as the PERSON may see it (§9.5): the two messages and nothing else.
 *
 * It is not `sessionTurnSchema.omit({ scribe: true })`, and the difference is
 * the point: « aucun indicateur de progression - ni numéro d'étape, ni
 * compteur de tours, ni statut d'objectif ». The snapshot, the decision, the
 * cost and the step are all progression, so none of them crosses. What is left
 * is a bubble: who said what, and when.
 */
export const sessionTurnViewSchema = z.object({
  id: z.string().min(1),
  message: z.string(),
  /** Null on the farewell turn, which nobody answers (issue #163, §8.1). */
  reply: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type SessionTurnView = z.infer<typeof sessionTurnViewSchema>;

/** `POST /api/s/{token}/message` - what the person just wrote (§9.5). */
export const sessionMessageSchema = z
  .object({
    text: z.string().trim().min(1).max(SESSION_MESSAGE_MAX_CHARS),
  })
  .strict();
export type SessionMessage = z.infer<typeof sessionMessageSchema>;

/**
 * One message of the conversation as `GET /api/s/{token}` serves it back: the
 * bubbles already played, so a reprise (§5.3) redraws the whole thread without
 * replaying a turn.
 */
export const sessionConversationMessageSchema = z.object({
  role: z.enum(['interviewer', 'person']),
  text: z.string(),
  at: z.string().datetime(),
  /**
   * What the message IS (prolongation.md §4.2, §5, issue #186). `turn` - the
   * default, and what every message served before the issue is - is a message of
   * the protocol conversation. `bubble` is the interviewer's bubble of a page
   * played in the conversation (`role: 'interviewer'`); `click` is what the
   * person pressed on such a page (`role: 'person'`): the text of the card taken,
   * or the values given then the label of the Bouton (§5).
   */
  kind: z.enum(['turn', 'bubble', 'click']).default('turn'),
  /** The page of the thread a `bubble` or a `click` belongs to; null on a `turn`. */
  pageId: z.string().nullable().default(null),
});
export type SessionConversationMessage = z.infer<typeof sessionConversationMessageSchema>;

/* ------------------------------------------------------------- the frames */

/**
 * « Un indicateur de frappe s'affiche entre l'envoi et le premier mot » (§9.5).
 *
 * It carries nothing: the client shows the dots until the first `delta`, and a
 * frame that named the turn would be a promise the engine cannot keep - the
 * turn has no id before the scribe has answered.
 */
export const sessionTypingEventSchema = z.object({ type: z.literal('typing') });
export type SessionTypingEvent = z.infer<typeof sessionTypingEventSchema>;

/**
 * One piece of the interviewer's message as the model writes it (§9.5): « la
 * réponse s'écrit dans la bulle à mesure ». Deltas and never the whole text,
 * the doctrine of the chat's socket; the REST view is what a reconnection
 * reads.
 */
export const sessionDeltaEventSchema = z.object({
  type: z.literal('delta'),
  turnId: z.string().min(1),
  text: z.string(),
});
export type SessionDeltaEvent = z.infer<typeof sessionDeltaEventSchema>;

/** The turn is written and stored - the bubble is final (§9.5). */
export const sessionMessageEventSchema = z.object({
  type: z.literal('message'),
  turn: sessionTurnViewSchema,
});
export type SessionMessageEvent = z.infer<typeof sessionMessageEventSchema>;

/**
 * Where the protocol page stands (§9.5), and the only progression the person
 * is told about.
 *
 * - `step` - the conversation goes on, whatever step it is on. Deliberately
 *   nameless: a step number on the wire is a step number in a DOM.
 * - `farewell` - the session has entered a farewell step (issue #163, §7.2):
 *   the goodbye is being written and nobody answers it. Published BEFORE its
 *   first delta, so the input disappears at the first word; `closed` follows
 *   the end of the stream. A reprise during the goodbye reopens on it (H80).
 * - `page` - the session is on a page played in the conversation (issue #186,
 *   prolongation.md §4.1): the person types nothing and only clicks. Published
 *   BEFORE the first delta of the page's bubble, so the input disappears at the
 *   first word, as on a goodbye. The farewell retained until the last click of
 *   the thread (§4.4) is served under `farewell` again, after the last page.
 * - `closed` - the protocol is over, « le dernier message de l'IA reste
 *   affiché et un bouton Continuer passe à la page suivante ».
 * - `experience-closed` - the session is closed (§8.4, §8.5): no next page.
 */
export const sessionProtocolStateSchema = z.enum([
  'step',
  'farewell',
  'page',
  'closed',
  'experience-closed',
]);
export type SessionProtocolState = z.infer<typeof sessionProtocolStateSchema>;

/**
 * The bubble of a page played in the conversation is COMPLETE (prolongation.md
 * §4.2, issue #186): the elements of the page can arrive under it.
 *
 * The bubble is written as a message is - `typing`, then `delta` frames, whose
 * `turnId` is the IDENTIFIER OF THE BUBBLE (the deltas of §9.5 name a bubble
 * before anything is stored, and a bubble row's id is that handle), then this
 * frame, which carries it whole. A reconnection reads the bubbles back by REST
 * and never replays a frame.
 *
 * Not `.strict()`, like every frame (#50).
 */
export const sessionBubbleEventSchema = z.object({
  type: z.literal('bubble'),
  bubble: z.object({
    id: z.string().min(1),
    pageId: z.string().min(1),
    message: z.string(),
    createdAt: z.string().datetime(),
  }),
});
export type SessionBubbleEvent = z.infer<typeof sessionBubbleEventSchema>;

export const sessionProtocolEventSchema = z.object({
  type: z.literal('protocol'),
  state: sessionProtocolStateSchema,
});
export type SessionProtocolEvent = z.infer<typeof sessionProtocolEventSchema>;

/**
 * Every frame of `GET /api/s/<jeton>/events` - the status one of the contract
 * of #50 (`schemas/session.ts`), plus the four the conversation needs.
 *
 * **Why the union lives here and not beside the status frame.** The `message`
 * frame carries a turn, the turn's snapshot carries the session's variables,
 * and `session.ts` is where those variables are defined. Composing the union
 * in `session.ts` would make the two files import each other, and a cycle
 * between two modules of Zod constants is not a style question: whichever one
 * Node enters first reads the other's schemas before they are initialised and
 * throws at import time. So the dependency runs ONE way - `turn.ts` reads
 * `session.ts` - and the union is assembled at the far end. Nothing moves for a
 * reader: `sessionEventSchema` is exported from `@matter_extractor/shared`
 * exactly as before.
 *
 * The tolerance rule of #50 still holds and is why no frame here is
 * `.strict()`: an older tab drops a `type` it does not know and strips a field
 * it does not know, so a deploy never blinds a conversation already open.
 *
 * The sixth is `noyau` (noyau.md §4.6, issue #147): the determination is
 * settled, and a tab whose advance was refused `noyau-pending` replays it. It is
 * declared beside the noyau's other shapes (`noyau.ts`), which import nothing,
 * and gathered here like the status frame.
 *
 * The seventh is `bubble` (prolongation.md §4.2, issue #186): the bubble of a
 * page played in the conversation, complete.
 */
export const sessionEventSchema = z.discriminatedUnion('type', [
  sessionStatusEventSchema,
  sessionTypingEventSchema,
  sessionDeltaEventSchema,
  sessionMessageEventSchema,
  sessionProtocolEventSchema,
  sessionNoyauEventSchema,
  sessionBubbleEventSchema,
]);
export type SessionEvent = z.infer<typeof sessionEventSchema>;
